import { useQuery } from '@tanstack/react-query';
import { api } from '../api';

interface Stats {
  activeSubscribers: number;
  mrrUsd: number;
  signupsToday: number;
  topTracks: Array<{ _id: string; title: string; plays: number }>;
  topArtists: Array<{ _id: string; stageName: string; totalPlays: number }>;
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="bg-surface border border-border rounded-xl p-6">
      <div className="text-text2 text-xs uppercase tracking-wide">{label}</div>
      <div className={`mt-2 text-3xl font-bold ${accent ? 'text-accent' : ''}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { data } = useQuery<Stats>({
    queryKey: ['admin', 'stats'],
    queryFn: () => api('/api/v1/admin/stats'),
  });

  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">Dashboard</h1>
      <div className="grid grid-cols-3 gap-4 mb-8">
        <Stat label="Active subscribers" value={String(data?.activeSubscribers ?? 0)} accent />
        <Stat label="MRR (USD)" value={`$${(data?.mrrUsd ?? 0).toFixed(0)}`} />
        <Stat label="Signups today" value={String(data?.signupsToday ?? 0)} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Card title="Top tracks">
          {data?.topTracks?.map((t) => (
            <div key={t._id} className="flex justify-between py-2 border-b border-border last:border-0">
              <span>{t.title}</span>
              <span className="text-text2">{t.plays.toLocaleString()}</span>
            </div>
          ))}
        </Card>
        <Card title="Top artists">
          {data?.topArtists?.map((a) => (
            <div key={a._id} className="flex justify-between py-2 border-b border-border last:border-0">
              <span>{a.stageName}</span>
              <span className="text-text2">{a.totalPlays.toLocaleString()}</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-surface border border-border rounded-xl p-6">
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      {children}
    </div>
  );
}
